import { useState } from "react";
import { Plus, Trash2, BookOpen } from "lucide-react";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { EventData, EventExtra, TicketOption } from "@/types/events";
import { RichTextEditor } from "@/design-system/forms/RichTextEditor";
import { ShortDescriptionField } from "./ShortDescriptionField";
import { supabase } from "@/integrations/supabase/client";
import { useMyOrganization } from "@/hooks/useMyOrganization";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface Props {
  event: EventData;
  updateEvent: (updates: Partial<EventData>) => void;
  tickets: TicketOption[];
  setTickets: React.Dispatch<React.SetStateAction<TicketOption[]>>;
  extras: EventExtra[];
  setExtras: React.Dispatch<React.SetStateAction<EventExtra[]>>;
}

export function ExtrasTicketsSection({ event, updateEvent, tickets, setTickets, extras, setExtras }: Props) {
  const { organization } = useMyOrganization();
  const orgId = organization?.id;

  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  const loadCatalog = async () => {
    setCatalogLoading(true);
    let query = (supabase as any)
      .from("extras_catalog")
      .select("*")
      .eq("is_active", true)
      .order("name", { ascending: true });
    if (orgId) {
      query = query.or(`organization_id.eq.${orgId},organization_id.is.null`);
    } else {
      query = query.is("organization_id", null);
    }
    const { data, error } = await query;
    if (!error) setCatalogItems(data ?? []);
    setCatalogLoading(false);
  };

  const openCatalog = () => {
    setSelectedIds({});
    setCatalogOpen(true);
    loadCatalog();
  };

  const addSelectedFromCatalog = () => {
    const chosen = catalogItems.filter((it) => selectedIds[it.id]);
    if (chosen.length > 0) {
      setExtras((prev) => [
        ...prev,
        ...chosen.map((it, idx) => ({
          name: it.name,
          // catalog prices are stored in cents; event extras use dollars
          price: (Number(it.price) || 0) / 100,
          description: it.description || "",
          sort_order: prev.length + idx,
          is_active: true,
          level: it.level ?? "team",
          requires_team: !!it.requires_team,
          requires_player: (it.level ?? "team") === "player" && !!it.requires_player,
          requires_game_date: !!it.requires_game_date,
        })),
      ]);
    }
    setCatalogOpen(false);
  };

  const addTicket = () => {
    setTickets((prev) => [
      ...prev,
      { name: "", price: 0, description: "", sort_order: prev.length, is_active: true },
    ]);
  };

  const updateTicket = (index: number, updates: Partial<TicketOption>) => {
    setTickets((prev) => prev.map((t, i) => (i === index ? { ...t, ...updates } : t)));
  };

  const removeTicket = (index: number) => {
    setTickets((prev) => prev.filter((_, i) => i !== index));
  };


  const addExtra = () => {
    setExtras((prev) => [
      ...prev,
      { name: "", price: 0, description: "", sort_order: prev.length, is_active: true },
    ]);
  };

  const updateExtra = (index: number, updates: Partial<EventExtra>) => {
    setExtras((prev) => prev.map((e, i) => (i === index ? { ...e, ...updates } : e)));
  };

  const removeExtra = (index: number) => {
    setExtras((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <SubSection title="Ticket Information">
        <ShortDescriptionField
          value={(event as any).tickets_short_description}
          onChange={(v) => updateEvent({ tickets_short_description: v } as any)}
        />
        <label className="flex items-center justify-between p-3 border border-border rounded-lg">
          <span className="text-sm font-medium">Use external ticket provider</span>
          <input
            type="checkbox"
            checked={!!(event as any).use_external_tickets}
            onChange={(e) => updateEvent({ use_external_tickets: e.target.checked } as any)}
            className="rounded"
          />
        </label>

        {(event as any).use_external_tickets && (
          <div className="pl-4 border-l-2 border-primary/20">
            <FieldLabel>External ticket purchase URL</FieldLabel>
            <input
              type="url"
              value={(event as any).external_tickets_url || ""}
              onChange={(e) => updateEvent({ external_tickets_url: e.target.value } as any)}
              placeholder="https://..."
              className={inputClass}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Link to your external ticket platform (e.g., Eventbrite, GameDay). When set, the public event page Spectator Tickets button will open this URL instead of an internal purchase modal.
            </p>
          </div>
        )}

        <div>
          <FieldLabel>Ticket Display Text</FieldLabel>
          <RichTextEditor
            value={event.ticket_display_text || ""}
            onChange={(html) => updateEvent({ ticket_display_text: html })}
            placeholder="Information about spectator tickets..."
            minHeight="100px"
          />
        </div>

        {tickets.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground font-medium">Spectator Tickets</p>
            {tickets.map((t, i) => (
              <div key={i} className="p-3 border border-border rounded-lg space-y-2">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Ticket name"
                      value={t.name}
                      onChange={(e) => updateTicket(i, { name: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div className="w-28">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <input
                        type="number"
                        placeholder="Price"
                        value={t.price || ""}
                        onChange={(e) => updateTicket(i, { price: parseFloat(e.target.value) || 0 })}
                        className={inputClass + " pl-7"}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => removeTicket(i)}
                    className="text-muted-foreground hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={t.description}
                  onChange={(e) => updateTicket(i, { description: e.target.value })}
                  className={inputClass}
                />
              </div>
            ))}
          </div>
        )}
        <button
          onClick={addTicket}
          className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Spectator Ticket
        </button>
      </SubSection>

      <SubSection title="Event Extras (Merchandise & Add-ons)">
        <ShortDescriptionField
          value={(event as any).extras_short_description}
          onChange={(v) => updateEvent({ extras_short_description: v } as any)}
        />
        {extras.length > 0 && (
          <div className="space-y-3">
            {extras.map((ex, i) => (
              <div key={i} className="p-3 border border-border rounded-lg space-y-2">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Extra name"
                      value={ex.name}
                      onChange={(e) => updateExtra(i, { name: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div className="w-28">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <input
                        type="number"
                        placeholder="Price"
                        value={ex.price || ""}
                        onChange={(e) => updateExtra(i, { price: parseFloat(e.target.value) || 0 })}
                        className={inputClass + " pl-7"}
                      />
                    </div>
                  </div>
                  <div className="w-24">
                    <input
                      type="number"
                      placeholder="Max qty"
                      value={ex.max_quantity_per_registration ?? ""}
                      onChange={(e) =>
                        updateExtra(i, {
                          max_quantity_per_registration: e.target.value ? parseInt(e.target.value) : undefined,
                        })
                      }
                      className={inputClass}
                    />
                  </div>
                  <button
                    onClick={() => removeExtra(i)}
                    className="text-muted-foreground hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <RichTextEditor
                  value={ex.description || ""}
                  onChange={(html) => updateExtra(i, { description: html })}
                  placeholder="Description"
                  minHeight="80px"
                />
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <p className="text-xs font-semibold text-foreground mb-2">Sold to</p>
                  <div className="flex flex-wrap gap-4 mb-3">
                    {(["team", "player"] as const).map((lvl) => (
                      <label key={lvl} className="flex items-center gap-2 text-sm capitalize">
                        <input
                          type="radio"
                          checked={(ex.level ?? "team") === lvl}
                          onChange={() =>
                            updateExtra(i, {
                              level: lvl,
                              ...(lvl === "team" ? { requires_player: false } : {}),
                            })
                          }
                        />
                        {lvl === "team" ? "Team (quantity only)" : "Player"}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs font-semibold text-foreground mb-2">Required info from buyer</p>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!ex.requires_team}
                        onChange={(e) => updateExtra(i, { requires_team: e.target.checked })}
                        className="rounded"
                      />
                      Team
                    </label>
                    <label
                      className={`flex items-center gap-2 text-sm ${
                        (ex.level ?? "team") === "team" ? "opacity-40" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={(ex.level ?? "team") === "team"}
                        checked={(ex.level ?? "team") === "player" && !!ex.requires_player}
                        onChange={(e) => updateExtra(i, { requires_player: e.target.checked })}
                        className="rounded"
                      />
                      Player
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!ex.requires_game_date}
                        onChange={(e) => updateExtra(i, { requires_game_date: e.target.checked })}
                        className="rounded"
                      />
                      Game date
                    </label>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">
                    e.g. a Video Package needs Team, Player, and Game date.
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={addExtra}
            className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Event Extra
          </button>
          <button
            onClick={openCatalog}
            className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
          >
            <BookOpen className="w-4 h-4" />
            Add from Catalog
          </button>
        </div>
      </SubSection>

      <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add from Extras Catalog</DialogTitle>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-2">
            {catalogLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            ) : catalogItems.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No catalog items found. Create some in the Extras Catalog first.
              </p>
            ) : (
              catalogItems.map((it) => {
                const tags: string[] = [];
                tags.push((it.level ?? "team") === "player" ? "Per player" : "Per team");
                if (it.requires_team) tags.push("Team");
                if (it.requires_player) tags.push("Player");
                if (it.requires_game_date) tags.push("Game date");
                return (
                  <label
                    key={it.id}
                    className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/40"
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedIds[it.id]}
                      onChange={(e) =>
                        setSelectedIds((prev) => ({ ...prev, [it.id]: e.target.checked }))
                      }
                      className="rounded mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">{it.name}</span>
                        <span className="text-sm text-muted-foreground">
                          ${((Number(it.price) || 0) / 100).toFixed(2)}
                        </span>
                      </div>
                      {it.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {it.description}
                        </p>
                      )}
                      {tags.length > 0 && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Requires: {tags.join(", ")}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatalogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={addSelectedFromCatalog}
              disabled={!Object.values(selectedIds).some(Boolean)}
            >
              Add selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
